
export interface DrawingCanvas {
  width: number;
  height: number;
  context: CanvasRenderingContext2D;
}

export const createDrawingCanvas = (width: number, height: number): DrawingCanvas | null => {
  const canvas = document.createElement('canvas');
  // Set high resolution dimensions
  const dpr = window.devicePixelRatio || 2;
  canvas.width = width * dpr;
  canvas.height = height * dpr;
  canvas.style.width = `${width}px`;
  canvas.style.height = `${height}px`;
  
  const ctx = canvas.getContext('2d', {
    alpha: true,
    desynchronized: false, // Better quality over performance
    willReadFrequently: false
  });
  
  if (!ctx) {
    console.error('Failed to get canvas context');
    return null;
  }
  
  // Scale everything by DPR for crisp rendering
  ctx.scale(dpr, dpr);
  
  // Enable high quality rendering
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  
  return {
    width,
    height,
    context: ctx
  };
};

export const drawRoundedRect = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
  fill: boolean = true,
  stroke: boolean = false
): void => {
  // Save previous settings
  ctx.save();
  
  // Use better rendering settings for shapes
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';
  
  ctx.beginPath();
  // Use proper arc drawing for rounded corners (more precise than quadratic curves)
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + width - radius, y);
  ctx.arcTo(x + width, y, x + width, y + radius, radius);
  ctx.lineTo(x + width, y + height - radius);
  ctx.arcTo(x + width, y + height, x + width - radius, y + height, radius);
  ctx.lineTo(x + radius, y + height);
  ctx.arcTo(x, y + height, x, y + height - radius, radius);
  ctx.lineTo(x, y + radius);
  ctx.arcTo(x, y, x + radius, y, radius);
  ctx.closePath();
  
  if (fill) {
    ctx.fill();
  }
  
  if (stroke) {
    // For better looking strokes
    ctx.lineWidth = Math.max(1, ctx.lineWidth);
    ctx.stroke();
  }
  
  // Restore previous settings
  ctx.restore();
};

// Create a clean light gray background
export const createGradientBackground = (
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number
): void => {
  // High quality gradient background
  const gradient = ctx.createLinearGradient(0, 0, 0, height);
  gradient.addColorStop(0, '#F9F9F9');
  gradient.addColorStop(1, '#F5F5F5');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, width, height);
  
  // Add subtle texture for visual interest
  ctx.globalAlpha = 0.02;
  ctx.fillStyle = '#6366f1';
  
  for (let x = 0; x < width; x += 40) {
    for (let y = 0; y < height; y += 40) {
      ctx.fillRect(x, y, 2, 2);
    }
  }
  ctx.globalAlpha = 1;
};
